import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { formatWithoutDecimal } from "../../utils/formatWithoutDecimal";

const SEGMENT_COLORS = [
  "#4F46E5",
  "#6366F1",
  "#818CF8",
  "#A78BFA",
  "#C084FC",
  "#F472B6",
  "#FB7185",
  "#F97316",
  "#14B8A6",
  "#22C55E",
  "#0EA5E9",
  "#F59E0B",
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const groupLabel = data.group || "Stock";
  return (
    <div
      style={{
        background: "#0F172A",
        color: "#F8FAFC",
        borderRadius: "8px",
        padding: "6px 10px",
        fontSize: "0.75rem",
        boxShadow: "0 10px 20px rgba(2, 6, 23, 0.25)",
        maxWidth: "180px",
        lineHeight: 1.2,
      }}
    >
      {groupLabel} - {data.name}:{" "}
      {formatWithoutDecimal(data.value)} units ({data.percentage}%)
    </div>
  );
};

const IndividualStock = ({ className = "", stockData }) => {
  const availableStock = stockData?.available_stock || {};
  const individualGroups = Array.isArray(stockData?.individual_stock_metal_group_wise)
    ? stockData.individual_stock_metal_group_wise
    : [];
  const bulkGroups = Array.isArray(stockData?.bulk_stock_metal_group_wise)
    ? stockData.bulk_stock_metal_group_wise
    : [];

  const outerBase = individualGroups
    .map((item, index) => ({
      name: item?.metal_group_name || `Individual ${index + 1}`,
      value: Number(item?.item_count ?? item?.pcs ?? 0),
      color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
    }))
    .filter((item) => Number.isFinite(item.value) && item.value > 0);
  const outerTotal = outerBase.reduce((sum, item) => sum + item.value, 0);
  const outerData = (
    outerBase.length > 0
      ? outerBase
      : [{ name: "Individual Stock", value: 1, color: "#A78BFA" }]
  ).map((item) => ({
    ...item,
    group: "Individual",
    percentage:
      outerTotal > 0 ? ((item.value / outerTotal) * 100).toFixed(1) : "100.0",
  }));

  const innerBase = bulkGroups
    .map((item, index) => ({
      name: item?.metal_group_name || `Bulk ${index + 1}`,
      value: Number(item?.item_count ?? item?.pcs ?? 0),
      color: SEGMENT_COLORS[(index + outerBase.length) % SEGMENT_COLORS.length],
    }))
    .filter((item) => Number.isFinite(item.value) && item.value > 0);
  const innerTotal = innerBase.reduce((sum, item) => sum + item.value, 0);
  const safeInnerData = (
    innerBase.length > 0
      ? innerBase
      : [
          {
            name: "Bulk Stock",
            value: Number(availableStock?.bulk_items ?? 0) || 1,
            color: "#4F46E5",
          },
        ]
  ).map((item) => ({
    ...item,
    group: "Bulk",
    percentage:
      innerTotal > 0 ? ((item.value / innerTotal) * 100).toFixed(1) : "100.0",
  }));

  return (
    <div className={`col-lg-3 col-md-5 col-12 ${className}`.trim()}>
      <div
        className="card shadow-none border-0"
        style={{
          minHeight: "285px",
          height: "285px",
          overflow: "hidden",
          borderRadius: "18px",
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
        }}
      >
        <div className="card-body px-4 pt-4 pb-3">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <span
                className="d-block"
                style={{
                  fontSize: "0.875rem",
                  color: "#334155",
                  fontWeight: 500,
                  letterSpacing: "0.2px",
                }}
              >
                Available Stock
              </span>
              <h5
                className="mb-0 mt-2"
                style={{
                  fontSize: "1.65rem",
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                {formatWithoutDecimal(availableStock?.total_items ?? 0)}
              </h5>
            </div>
          </div>

          <div style={{ marginTop: "2px", height: "160px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <Pie
                  data={safeInnerData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={34}
                  outerRadius={50}
                  paddingAngle={2}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  isAnimationActive
                  animationDuration={700}
                >
                  {safeInnerData.map((segment, index) => (
                    <Cell key={`inner-${index}`} fill={segment.color} />
                  ))}
                </Pie>
                <Pie
                  data={outerData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={56}
                  outerRadius={74}
                  paddingAngle={1.5}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  isAnimationActive
                  animationDuration={850}
                >
                  {outerData.map((segment, index) => (
                    <Cell key={`outer-${index}`} fill={segment.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ zIndex: 20, outline: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualStock;
